import { constructValue } from '@adornis/baseql/entities/construct.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DateTime } from 'luxon';
import { ChancenRole } from '../../../semantics/ChancenRole.js';
import { Taetigkeit } from '../../../semantics/Taetigkeit.js';

const CACHE_TTL = 1000 * 60 * 5;
const CACHE_MAX_SIZE = 1000;
const CACHE = new Map<
  string,
  {
    taetigkeiten: Taetigkeit[];
    lastUpdated: number;
  }
>();

function evictExpiredCacheEntries() {
  const now = Date.now();
  for (const [key, value] of CACHE) {
    if (now - value.lastUpdated >= CACHE_TTL) {
      CACHE.delete(key);
    }
  }
}

export const getEmployeesTaetigkeiten = registerQuery({
  type: () => [Taetigkeit],
  operationName: 'getEmployeesTaetigkeiten',
  resolve: (kpfID: string, predicateID: string, from: DateTime = DateTime.now()) => {
    return async (gqlFields: BaseQLSelectionSet<Taetigkeit>) => {
      const cached = CACHE.get(`${kpfID}-${predicateID}-${from.toFormat('yyyy-MM-dd')}`);
      if (cached && Date.now() - cached.lastUpdated < CACHE_TTL) {
        return cached.taetigkeiten;
      }

      const predicate = (await loadMongoData(ChancenRole, { _id: predicateID })).at(0);
      if (!predicate) return [];

      const TaetigkeitClass = (await import('../../../semantics/Taetigkeit.js')).Taetigkeit;

      const relationshipsForKPF = await loadMongoData(TaetigkeitClass, {
        objectID: kpfID,
        predicateID: predicate._id,
        ...TaetigkeitClass.isActiveMongoSelector(from),
      });

      // Evict expired entries and enforce max cache size
      if (CACHE.size >= CACHE_MAX_SIZE) {
        evictExpiredCacheEntries();
        // If still over limit, remove oldest entries
        if (CACHE.size >= CACHE_MAX_SIZE) {
          const iterator = CACHE.keys();
          const key = iterator.next().value;
          if (key) CACHE.delete(key);
        }
      }

      CACHE.set(`${kpfID}-${predicateID}-${from.toFormat('yyyy-MM-dd')}`, {
        taetigkeiten: relationshipsForKPF.map(user => constructValue(user) as Taetigkeit),
        lastUpdated: Date.now(),
      });

      return relationshipsForKPF?.map(user => constructValue(user) as Taetigkeit);
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'kpfID' },
    { type: () => String, name: 'predicateID' },
    { type: () => DateTime, name: 'from' },
  ],
});
